import { useEffect, useRef, useState } from 'react';
import { events } from 'aws-amplify/data';
import type { EventsChannel } from 'aws-amplify/data';
import { Amplify } from 'aws-amplify';

import { productDb as db } from '../db';
import { isStoreProduct, isValidISODateString } from '../helpers';
import { StoreProduct } from '@bofrak-backend/shared';
import { WebSocketStatus } from '../types';

interface UseAppSyncEventsArgs {
  storeId: string;
  enabled?: boolean;
  onDiscountChange?: (product: StoreProduct) => void | Promise<void>;
}

interface UseAppSyncEventsReturn {
  status: WebSocketStatus;
  error: unknown | null;
  lastMessage: StoreProduct | null;
  channelRef: EventsChannel | null;
  send: (payload: unknown) => Promise<boolean>;
}

Amplify.configure({
  API: {
    Events: {
      endpoint: import.meta.env.VITE_APPSYNC_HTTP_DOMAIN,
      region: import.meta.env.VITE_REGION,
      defaultAuthMode: 'apiKey',
      apiKey: import.meta.env.VITE_APPSYNC_API_KEY,
    },
  },
});

export function useAppSyncProductEventsWebsockets({
  storeId,
  enabled = true,
  onDiscountChange,
}: UseAppSyncEventsArgs): UseAppSyncEventsReturn {
  const [status, setStatus] = useState<WebSocketStatus>({
    connected: false,
    reconnectAttempts: 0,
    lastUpdate: undefined,
  });

  const [error, setError] = useState<unknown | null>(null);
  const [lastMessage, setLastMessage] = useState<StoreProduct | null>(null);
  const [channelRef, setChannelRef] = useState<EventsChannel | null>(null);

  const subscriptionRef = useRef<ReturnType<EventsChannel['subscribe']> | null>(
    null,
  );

  useEffect(() => {
    if (!enabled || !storeId) return;

    let channelInstance: EventsChannel;

    const connectAndSubscribe = async () => {
      try {
        channelInstance = await events.connect(`/products/${storeId}`);
        setChannelRef(channelInstance);

        setStatus((s) => ({ ...s, connected: true }));

        if (!subscriptionRef.current) {
          subscriptionRef.current = channelInstance.subscribe({
            next: async (data) => {
              const evt = data.event;

              console.log('AppSync Event received:', evt);

              if (isStoreProduct(evt)) {
                if (evt.store_id !== storeId) return;
                setLastMessage(evt);

                await db.transaction(
                  'rw',
                  db.storeProducts,
                  db.productBarcodes,
                  async () => {
                    if (!evt.is_parent) {
                      const parent = await db.storeProducts.get([
                        evt.store_id,
                        evt.parent_id,
                      ]);
                      if (!parent) return;

                      const fractions = [
                        ...(parent.store_product_fractions ?? []),
                      ];
                      const index = fractions.findIndex(
                        (fraction) => fraction.id === evt.id,
                      );
                      if (isValidISODateString(evt.deleted_at)) {
                        if (index >= 0) fractions.splice(index, 1);
                      } else if (index >= 0) {
                        fractions[index] = { ...fractions[index], ...evt };
                      } else {
                        fractions.push({
                          ...evt,
                          fraction_id: evt.id,
                          fraction_name: evt.name,
                          parent_name: parent.name,
                        });
                      }

                      const updatedParent = {
                        ...parent,
                        store_product_fractions: fractions,
                        _lastSynced: new Date(),
                      };
                      await db.storeProducts.put(updatedParent);
                      await db.updateBarcodeIndexForProduct(updatedParent);
                      return;
                    }

                    if (isValidISODateString(evt.deleted_at)) {
                      const fractionIds = (
                        evt.store_product_fractions ?? []
                      ).map((f: StoreProduct) => f.id);
                      await db.storeProducts.delete([evt.store_id, evt.id]);
                      await db.removeBarcodeIndexForProduct(evt.store_id, [
                        evt.id,
                        ...fractionIds,
                      ]);
                    } else {
                      await db.storeProducts.put(evt);
                      await db.updateBarcodeIndexForProduct(evt);
                    }
                  },
                );

                if (evt.discount_updated_at) {
                  await onDiscountChange?.(evt);
                }

                // Update status
                setStatus((s) => ({
                  ...s,
                  lastUpdate: new Date(),
                }));
              }
            },
            error: (err) => {
              setError(err);
              setStatus((s) => ({ ...s, connected: false }));
              console.error('AppSync Events subscription error:', err);
            },
          });
        }
      } catch (err) {
        setError(err);
        setStatus((s) => ({ ...s, connected: false }));
        console.error('Error connecting to AppSync Events:', err);
      }
    };

    connectAndSubscribe();

    return () => {
      subscriptionRef.current?.unsubscribe();
      subscriptionRef.current = null;

      channelInstance?.close();
    };
  }, [storeId, enabled, onDiscountChange]);

  /**
   * Publish to AppSync Events API
   */
  const send = async (payload: unknown): Promise<boolean> => {
    try {
      if (!channelRef) return false;

      await channelRef.publish(
        typeof payload === 'string' ? payload : JSON.stringify(payload),
      );

      return true;
    } catch (err) {
      console.error('Error publishing to AppSync Events:', err);
      setError(err);
      return false;
    }
  };

  return {
    status,
    error,
    lastMessage,
    channelRef,
    send,
  };
}
